const ESC = 0x1b;
const GS = 0x1d;
const LINE_WIDTH = 30;

function escposInit(): number[] {
  return [ESC, 0x40];
}
function escposCenterOn(): number[] {
  return [ESC, 0x61, 1];
}
function escposCenterOff(): number[] {
  return [ESC, 0x61, 0];
}
function escposBoldOn(): number[] {
  return [ESC, 0x45, 1];
}
function escposBoldOff(): number[] {
  return [ESC, 0x45, 0];
}
function escposNormal(): number[] {
  return [ESC, 0x21, 0x00];
}
function escposCut(): number[] {
  return [GS, 0x56, 0x00];
}
function escposFeed(lines: number): number[] {
  return [ESC, 0x64, lines];
}
function textToBytes(text: string): number[] {
  const bytes: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    bytes.push(code > 127 ? 63 : code);
  }
  return bytes;
}
function escposLine(text: string): number[] {
  return [...textToBytes(text), 0x0a];
}
function escposDashedLine(): number[] {
  return escposLine("-".repeat(LINE_WIDTH));
}
function wrapText(text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let remaining = text;
  while (remaining.length > maxWidth) {
    let breakAt = remaining.lastIndexOf(" ", maxWidth);
    if (breakAt <= 0) breakAt = maxWidth;
    lines.push(remaining.slice(0, breakAt));
    remaining = remaining.slice(breakAt).trimStart();
  }
  if (remaining.length > 0) lines.push(remaining);
  return lines;
}
function spreadText(text: string, width: number): string {
  if (text.length >= width) return text;
  const gaps = text.length - 1;
  if (gaps <= 0) return text.padEnd(width);
  const totalSpaces = width - text.length;
  const baseSpaces = Math.floor(totalSpaces / gaps);
  const extraSpaces = totalSpaces % gaps;
  let result = "";
  for (let i = 0; i < text.length; i++) {
    result += text[i];
    if (i < gaps) {
      result += " ".repeat(baseSpaces + (i < extraSpaces ? 1 : 0));
    }
  }
  return result;
}
function escposBarcodeCode128(data: string): number[] {
  const dataBytes = Array.from(data).map((c) => c.charCodeAt(0));
  return [
    GS,
    0x68,
    80,
    GS,
    0x77,
    2,
    GS,
    0x48,
    0,
    GS,
    0x6b,
    0x49,
    dataBytes.length,
    ...dataBytes,
  ];
}

/**
 * ESC/POS QR Code commands per Epson spec.
 * Uses GS ( k subcommands 165 (model), 167 (size), 169 (error correction),
 * 180 (store data), 181 (print). Most modern thermal printers (2018+) support
 * this. Older / no-name printers may print blank.
 */
function escposQRCode(data: string, moduleSize: number = 6): number[] {
  const dataBytes = Array.from(data).map((c) => c.charCodeAt(0));
  const len = dataBytes.length + 3;
  const pL = len & 0xff;
  const pH = (len >> 8) & 0xff;

  return [
    // Set QR model — model 2
    GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00,
    // Set QR module size — 1..16 (default 6)
    GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, Math.max(1, Math.min(16, moduleSize)),
    // Set QR error correction — L (48), M (49), Q (50), H (51). Use M.
    GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, 0x31,
    // Store QR data
    GS, 0x28, 0x6b, pL, pH, 0x31, 0x50, 0x30, ...dataBytes,
    // Print stored QR
    GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30,
  ];
}

export function generateAppBarcode(): string {
  const ts = Date.now().toString().slice(-8);
  const rand = Math.floor(Math.random() * 9000 + 1000).toString();
  return `DH${ts}${rand}`;
}

export function buildBarcodeEscPos(
  products: {
    name: string;
    barcode: string;
    qty: number;
    barcodeType?: "code128" | "qr" | "both";
    storeName?: string;
    salePrice?: number;
    mrp?: number;
  }[],
): Uint8Array {
  const d: number[] = [];
  d.push(...escposInit());

  for (const product of products) {
    const type = product.barcodeType ?? "code128";
    for (let q = 0; q < product.qty; q++) {
      d.push(...escposCenterOn());
      d.push(...escposBoldOn());
      d.push(...escposLine(product.name.toUpperCase().slice(0, LINE_WIDTH)));
      d.push(...escposBoldOff());

      if (type === "code128" || type === "both") {
        d.push(...escposBarcodeCode128(product.barcode));
        d.push(...escposLine(spreadText(product.barcode, LINE_WIDTH)));
      }

      if (type === "qr" || type === "both") {
        d.push(...escposQRCode(product.barcode, 6));
        d.push(...escposLine(product.barcode));
      }

      // Optional price line for QR/both flows.
      if (type === "qr" || type === "both") {
        const mrpVal = product.mrp ?? product.salePrice;
        const parts: string[] = [];
        if (mrpVal !== undefined) parts.push(`MRP: ${Number(mrpVal).toFixed(2)}`);
        if (product.salePrice !== undefined) parts.push(`Sale: ${Number(product.salePrice).toFixed(2)}`);
        if (parts.length > 0) d.push(...escposLine(parts.join("  ")));
      }

      d.push(...escposCenterOff());
      d.push(...escposNormal());
      d.push(ESC, 0x4a, 172);
    }
  }

  d.push(...escposFeed(4));
  d.push(...escposCut());
  return new Uint8Array(d);
}
